import { useRef, useState } from 'react'
import { faCircleCheck } from '@fortawesome/pro-solid-svg-icons'
import { Icon, Text } from '@/ui'
import { TextColors, TypographyStyles } from '@/styles'
import { WIDGET_REGISTRY } from './registry'
import { ToolInvocationState } from '../../lib/constants'
import type { ToolInvocation, WidgetDisplay } from '../../lib/types'

interface WidgetHostProps {
  invocation: ToolInvocation
  disabled?: boolean
  /** A newer call of the same widget is on screen, so this one can no longer be answered. */
  superseded?: boolean
  /** Resolves false when the server refused the outcome, so the widget reopens. */
  onOutcome: (
    invocation: { toolCallId: string; toolName: string },
    text: string,
    display?: WidgetDisplay
  ) => Promise<boolean>
  onFollowUp?: (text: string) => void
  /** Haven is writing a reply anywhere in the chat, not only in this widget's message. */
  streaming?: boolean
}

const RecordRow = ({ display }: { display: WidgetDisplay }) => (
  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 rounded-[8px] border border-neutral-200 bg-neutral-25 px-3 py-2">
    <div className="flex min-w-0 grow basis-auto items-center gap-2">
      <Icon icon={faCircleCheck} className="text-xs text-neutral-400" />
      <Text
        variant={TypographyStyles.BODY_SM}
        color={TextColors.MUTED}
        className="min-w-0 [overflow-wrap:anywhere]"
      >
        {display.label}
      </Text>
    </div>
    {display.meta && (
      <Text
        variant={TypographyStyles.BODY_SM}
        color={TextColors.MUTED}
        className="ml-auto tabular-nums"
      >
        {display.meta}
      </Text>
    )}
  </div>
)

/** The open/closed state, the outcome line and the report-once latch every widget shares. */
export const WidgetHost = ({
  invocation,
  disabled,
  superseded = false,
  onOutcome,
  onFollowUp,
  streaming = false,
}: WidgetHostProps) => {
  const [reported, setReported] = useState<string | undefined>(undefined)
  const [reportedDisplay, setReportedDisplay] = useState<
    WidgetDisplay | undefined
  >(undefined)
  // State lags a render, so two clicks before one lands would both get through.
  const latched = useRef(false)
  const Widget = WIDGET_REGISTRY[invocation.toolName]
  const { toolCallId } = invocation

  // The model retries a call it sent bad input for, so the failed one draws nothing.
  if (
    !Widget ||
    !toolCallId ||
    invocation.state === 'input-streaming' ||
    invocation.state === ToolInvocationState.OutputError
  )
    return null

  const outcome =
    reported ??
    (typeof invocation.result === 'string' ? invocation.result : undefined)
  const resolved = outcome !== undefined || Boolean(disabled) || superseded

  const display = reportedDisplay ?? invocation.display

  const handleOutcome = async (text: string, shown?: WidgetDisplay) => {
    if (resolved || latched.current) return true
    latched.current = true
    setReported(text)
    setReportedDisplay(shown)
    const accepted = await onOutcome(
      { toolCallId, toolName: invocation.toolName },
      text,
      shown
    )
    if (accepted === false) {
      latched.current = false
      setReported(undefined)
      setReportedDisplay(undefined)
    }
    return accepted !== false
  }

  return (
    <div className="my-2 flex flex-col gap-3">
      <Widget
        input={
          typeof invocation.input === 'object' &&
          invocation.input !== null &&
          !Array.isArray(invocation.input)
            ? (invocation.input as Record<string, unknown>)
            : {}
        }
        resolved={resolved}
        outcome={outcome}
        toolCallId={toolCallId}
        onOutcome={handleOutcome}
        superseded={superseded}
        busy={Boolean(disabled) || streaming}
        onFollowUp={onFollowUp}
      />

      {outcome === undefined && superseded && (
        <Text
          variant={TypographyStyles.BODY_SM}
          color={TextColors.MUTED}
          className="border-t border-neutral-100 pt-3"
        >
          A newer request below replaces this one.
        </Text>
      )}

      {outcome !== undefined && display && <RecordRow display={display} />}

      {outcome !== undefined && !display && (
        <Text
          variant={TypographyStyles.BODY_SM}
          color={TextColors.MUTED}
          className="whitespace-pre-line border-t border-neutral-100 pt-3"
        >
          {outcome}
        </Text>
      )}
    </div>
  )
}
